import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { INTEGRATIONS_TABS } from '@/Pages/tabRoutes';
import ProvidersPanel from './components/ProvidersPanel';
import InstallationsPanel from './components/InstallationsPanel';
import WebhooksPanel from './components/WebhooksPanel';
import ApiClientsPanel from './components/ApiClientsPanel';

/**
 * §2 / §3 — the integration substrate (ADR-0002) and outbound webhooks
 * (ADR-0004), one screen.
 *
 * They are two backend modules with two controllers and two permission
 * families, but ADR-0004 itself names the reason they share a screen: its
 * "Dead-letter and health UI" section says that surface belongs to "the
 * Integration Health Center (blueprint §2)", not to a screen of its own. A
 * venue that has installed Stripe and pointed a webhook at its accountant's
 * inbox wants one place to ask "is anything broken right now", not two.
 *
 * Three tabs: **Providers** is the catalogue a venue installs from.
 * **Installations** is what this venue has actually connected — health,
 * credentials, sync and the per-installation dead-letter queue all live in
 * the detail dialog one row opens. **Webhooks** is the venue's own outbound
 * endpoints — secrets, the delivery log, and the auto-disabled state.
 *
 * **API clients** (ADR-0003 / ADR-0027) is the mirror image of Webhooks: not
 * this venue sending events out, but a partner's server reading or writing
 * this venue's data on `/public/v1`, on a credential this venue mints and can
 * kill. It rides the same "secret shown once" and rotate/revoke shapes
 * Webhooks already established rather than living under Settings — see
 * `tabRoutes.ts`'s `INTEGRATIONS_TABS` for the fuller argument.
 */
export default function Integrations() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/integrations', INTEGRATIONS_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('integrations.title')}</h1>
        <p className="text-sm text-ink-muted">{t('integrations.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('integrations.title')}
        idPrefix="integrations"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'providers' && <ProvidersPanel />}
      {current === 'installations' && <InstallationsPanel />}
      {current === 'webhooks' && <WebhooksPanel />}
      {current === 'api-clients' && <ApiClientsPanel />}
    </div>
  );
}
